import { Component, ChangeDetectionStrategy, inject, output, signal, ChangeDetectorRef, ElementRef, ViewChild, HostListener } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { LanguageService } from '../../services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MasterService } from '@/src/services/master.service';
import { AuthenticationService } from '@/src/services/authentication.service';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { M_STORE } from '@/src/models/M_STORE';
import { Constants } from '@/src/helpers/constants';
import { InputTextModule } from 'primeng/inputtext';
import { Popover, PopoverModule } from 'primeng/popover';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { PasswordModule } from 'primeng/password';
import { AccountService } from '@/src/services/account.service';
import { PopUpCommonComponent } from '../pop-up-common/pop-up-common.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [TranslateModule, CommonModule, TableModule, DialogModule, InputTextModule, Popover,
    PopoverModule, PasswordModule, ReactiveFormsModule, PopUpCommonComponent, FormsModule],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private router: Router = inject(Router);
  private masterService = inject(MasterService)
  @ViewChild('langWrapper') langWrapper!: ElementRef;
  visible = false;
  currentLang = '';
  today: any;
  userName: any;
  showStore: boolean = false;
  P_PAGEROW = Constants.P_PAGEROW_POPUP;
  param = {
    P_HQ_CD: "",
    P_USE_YN: "Y",
    P_STR_CD: "",
    P_STR_NM: "",
    P_PAGEINDEX: 1,
    P_PAGEROW: this.P_PAGEROW
  }
  isAdminLogin: boolean = false;
  strNm: string = '';

  toggleMobileMenu = output<void>();
  useForm: FormGroup;
  langDropdownOpen = signal(false);

  constructor(private translate: TranslateService, private auth: AuthenticationService, private cdr: ChangeDetectorRef,
    private fb: FormBuilder, private accountSvc: AccountService) {
    this.today = this.masterService.formatDateYYYYMMDD3(new Date())
    this.currentLang = localStorage.getItem(Constants.Language) || "en"
    this.isAdminLogin = localStorage.getItem(Constants.IsAdminLogin) === "Y"
    this.strNm = localStorage.getItem(Constants.strNm) || '';

    this.useForm = this.fb.group(
      {
        CurrentPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=\S+$)(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/)
          ],
        ],
        Password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=\S+$)(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/)
          ],
        ],
        ConfirmPassword: ['', [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=\S+$)(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
          this.matchPasswords()
        ]],
      },
      { validators: this.matchPasswords() }
    );
  }

  ngOnInit() {
    this.getUserInfo()
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (!this.langWrapper) return;

    if (!this.langWrapper.nativeElement.contains(event.target)) {
      this.langDropdownOpen.set(false);
    }
  }
  @ViewChild('op') op!: Popover;
  currUser: any;
  toggle(event: any) {
    this.currUser = this.auth.currentUser();
    this.op.toggle(event);
  }

  matchPasswords(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get('Password')?.value;
      const confirmPassword = group.get('ConfirmPassword')?.value;

      if (password !== confirmPassword) {
        group.get('ConfirmPassword')?.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      }

      if (group.get('ConfirmPassword')?.hasError('passwordMismatch')) {
        group.get('ConfirmPassword')?.setErrors(null);
      }

      return null;
    };
  }

  getUserInfo() {
    this.masterService.getUserInfo().subscribe((res: any) => {
      if (res?.isOK) {
        const data = res?.result?.dataRes[0]?.rows[0]
        this.userName = data?.useR_NM ?? this.auth.getUserNm()
        this.cdr.detectChanges();
      }
    })
  }

  resetForm() {
    this.useForm.reset()
  }
  showConfirmLogin = false;
  changePass() {
    const curr = this.auth.currentUser();
    if (this.useForm.valid) {
      this.accountSvc.changePasswordByUsername(curr.userName, this.useForm.value.Password, this.useForm.value.CurrentPassword).subscribe((res: any) => {
        if (res?.isOK) {
          // this.masterService.showSuccess(this.translate.instant('LOGINHQ0030'))
          localStorage.removeItem(Constants.CurrentUser);
          sessionStorage.removeItem(Constants.CurrentUser);
          this.visible = false;
          this.showConfirmLogin = true;
          this.cdr.detectChanges();
        }
        else {
          this.masterService.showError(Constants.showError(res?.errorMessages))
        }
      })
    }
    else {
      this.useForm.markAllAsTouched()
    }
  }

  logout() {
    this.showConfirmLogin = false;
    this.auth.logout();
    location.reload();
  }

  private getRouteTitle(route: ActivatedRoute): string | undefined {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.data['titleKey'] || route.snapshot.title;
  }

  titleKey = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.getRouteTitle(this.router.routerState.root) || 'dashboard'),
      startWith(this.getRouteTitle(this.router.routerState.root) || 'dashboard')
    ),
    { initialValue: 'dashboard' }
  );

  setLanguage(lang: 'en' | 'ko' | 'vi') {
    this.currentLang = lang;
    this.langDropdownOpen.set(false);
    localStorage.setItem(Constants.Language, lang);
    this.translate.use(lang);
    location.reload();
  }
  stores = new Array<M_STORE>();
  @ViewChild('tb') table!: any;
  totalCnt = 0;
  getStore(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    const hqCd = this.auth.getHQCD();
    if (hqCd != '') {
      this.showStore = true;
      this.param.P_HQ_CD = hqCd;
      this.param.P_STR_CD = this.param.P_STR_NM.length > 7 ? this.param.P_STR_NM.slice(0, 7) : this.param.P_STR_NM;
      this.param.P_PAGEINDEX = P_PAGEINDEX;
      //6a839a25-298d-11f1-9014-00155d001e02
      this.masterService.excute_sp('6a839a25-298d-11f1-9014-00155d001e03', this.param).subscribe((res: any) => {
        if (res?.isOK) {
          const newData = res?.result?.dataRes[1]?.rows || [];
          this.totalCnt = res?.result?.dataRes[0]?.rows[0]?.totalcnt
          this.stores = new M_STORE().getAllStores(newData);
          if (this.table && !isNextPage) {
            this.table.reset();
          }
          this.cdr.detectChanges();
        }
      })
    }
  }

  loadPage(event: any) {
    const page = (event.first / event.rows) + 1;
    this.getStore(page, true);
  }

  selectStore(store: M_STORE) {
    const user = this.auth.currentUser();
    user.stR_CD = store.stR_CD;
    let userEncrypt = Constants.encryptWithKey(JSON.stringify(user), this.auth.getPrivateKeyUser());
    sessionStorage.setItem(Constants.CurrentUser, userEncrypt);
    localStorage.setItem(Constants.strNm, store.stR_NM);
    this.showStore = false;
    localStorage.removeItem(Constants.CartTemp)
    location.reload();
  }
}